"use client";

import { useEffect, useRef, useState, type ReactNode } from "react";
import { motion, useInView, useReducedMotion } from "framer-motion";

/**
 * Three animated mock ATS screens: knockouts, keywords, competencies.
 * Each card plays once when scrolled into view. Reduced motion shows the final state.
 */

const GREEN = "#059669";
const RED = "#dc2626";
const NAVY = "#143f80";
const SLATE = "#334155";
const EASE = [0.16, 1, 0.3, 1] as const;

// Seconds between one card dropping in and the next.
const CARD_GAP_MS = 4000;

function useSequence(enabled: boolean) {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, amount: 0.5 });
  const reduce = useReducedMotion();
  // A card plays once it is both visible and its turn has come.
  const on = (inView && enabled) || !!reduce;
  // Delay in seconds; instant when the visitor prefers reduced motion.
  const t = (delay: number, duration = 0.5) =>
    reduce ? { duration: 0 } : { delay, duration, ease: EASE };
  const spring = (delay: number) =>
    reduce ? { duration: 0 } : { delay, type: "spring" as const, stiffness: 320, damping: 15 };
  return { ref, on, reduce: !!reduce, t, spring };
}

function Check({ on, delay, color, reduce }: { on: boolean; delay: number; color: string; reduce: boolean }) {
  return (
    <svg viewBox="0 0 24 24" className="h-5 w-5 shrink-0 overflow-visible" aria-hidden>
      <motion.path
        d="M4 12 L10 18 L20 6"
        fill="none"
        stroke={color}
        strokeWidth={3.5}
        strokeLinecap="round"
        strokeLinejoin="round"
        initial={{ pathLength: 0 }}
        animate={{ pathLength: on ? 1 : 0 }}
        transition={reduce ? { duration: 0 } : { delay, duration: 0.4, ease: "easeOut" }}
      />
    </svg>
  );
}

function Card({
  label,
  term,
  definition,
  fix,
  children,
  innerRef,
  on,
  reduce,
  shakeDelay,
}: {
  label: string;
  term: string;
  definition: string;
  fix: string;
  children: ReactNode;
  innerRef: React.RefObject<HTMLDivElement | null>;
  on: boolean;
  reduce: boolean;
  shakeDelay?: number;
}) {
  const shake = on && !reduce && shakeDelay !== undefined;
  return (
    <motion.div
      ref={innerRef}
      className="relative flex flex-col rounded-2xl bg-white p-7"
      style={{ border: "1px solid rgba(20, 63, 128, 0.08)", boxShadow: "0 2px 16px rgba(20,63,128,0.05)" }}
      initial={{ opacity: 0, y: -48, x: 0 }}
      animate={{ opacity: on ? 1 : 0, y: on ? 0 : -48, x: shake ? [0, -10, 10, -7, 7, -3, 0] : 0 }}
      transition={
        reduce
          ? { duration: 0 }
          : {
              opacity: { duration: 0.6, ease: "easeOut" },
              y: { duration: 0.8, ease: EASE },
              x: { delay: shakeDelay ?? 0, duration: 0.45, ease: "easeInOut" },
            }
      }
      whileHover={{ y: -4 }}
    >
      <p className="mb-3 text-xs font-extrabold uppercase" style={{ color: "#64748b", letterSpacing: "0.16em" }}>
        {label}
      </p>
      <div className="flex min-h-[210px] flex-col">{children}</div>
      <h3 className="mt-6 text-xl font-bold" style={{ color: NAVY }}>
        {term}
      </h3>
      <p className="mt-2 text-base leading-relaxed" style={{ color: SLATE }}>
        {definition}
      </p>
      <p className="mt-auto pt-3 text-base font-semibold leading-relaxed" style={{ color: GREEN }}>
        {fix}
      </p>
    </motion.div>
  );
}

function Pill({
  children,
  tone,
  on,
  transition,
  filled,
}: {
  children: ReactNode;
  tone: "red" | "green";
  on: boolean;
  transition: object;
  filled?: boolean;
}) {
  const color = tone === "red" ? RED : GREEN;
  return (
    <motion.span
      className="mt-auto inline-block self-start rounded-full px-4 py-1 text-sm font-extrabold"
      style={{ border: `3px solid ${color}`, background: filled ? color : "transparent", color: filled ? "#fff" : color }}
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: on ? 1 : 0, y: on ? 0 : 12 }}
      transition={transition}
    >
      {children}
    </motion.span>
  );
}

function KnockoutCard({ enabled }: { enabled: boolean }) {
  const { ref, on, reduce, t, spring } = useSequence(enabled);
  return (
    <Card
      innerRef={ref}
      on={on}
      reduce={reduce}
      label="Knockout question"
      term="Knockouts"
      definition="These are pass/fail questions like work authorization, location, salary, and degree. One wrong answer and the ATS rejects you, no matter how strong your resume is."
      fix="We flag the ones you may fail before you apply."
      shakeDelay={1.7}
    >
      <p className="mb-3 font-semibold" style={{ color: "#1e293b" }}>
        Are you authorized to work in the United States?
      </p>
      <div className="mb-4 flex gap-3">
        <span className="rounded-full px-4 py-1 text-sm font-extrabold" style={{ border: "3px solid #cbd5e1", color: SLATE }}>
          Yes
        </span>
        <motion.span
          className="rounded-full px-4 py-1 text-sm font-extrabold"
          style={{ border: "3px solid #cbd5e1" }}
          animate={
            on
              ? { backgroundColor: RED, borderColor: RED, color: "#ffffff" }
              : { backgroundColor: "rgba(255,255,255,0)", borderColor: "#cbd5e1", color: SLATE }
          }
          transition={reduce ? { duration: 0 } : { delay: 1.0, duration: 0.25 }}
        >
          No
        </motion.span>
      </div>
      <Pill tone="red" filled on={on} transition={t(1.7, 0.3)}>
        Rejected
      </Pill>
      <motion.span
        className="pointer-events-none absolute right-4 top-12 rounded-full px-3 py-0.5 text-lg font-extrabold"
        style={{ border: `4px solid ${RED}`, color: RED, background: "rgba(255,255,255,0.8)", fontFamily: "var(--font-caveat, cursive)" }}
        initial={{ opacity: 0, scale: 2.4, rotate: 20 }}
        animate={on ? { opacity: 1, scale: 1, rotate: -8 } : { opacity: 0, scale: 2.4, rotate: 20 }}
        transition={spring(1.6)}
        aria-hidden
      >
        Knocked out
      </motion.span>
    </Card>
  );
}

const KEYWORDS: { text: string; hit: boolean }[] = [
  { text: "budget forecasting", hit: true },
  { text: "Excel", hit: true },
  { text: "vendor management", hit: false },
];

function KeywordCard({ enabled }: { enabled: boolean }) {
  const { ref, on, reduce, t } = useSequence(enabled);
  return (
    <Card
      innerRef={ref}
      on={on}
      reduce={reduce}
      label="Keywords"
      term="Keywords"
      definition="Missing keywords from the job post don't get you rejected, but they rank you lower in the pile."
      fix="We show you exactly which ones are in your resume and which are missing."
    >
      <p className="mb-3 font-semibold" style={{ color: "#1e293b" }}>
        The job post asks for:
      </p>
      <div className="mb-4 flex flex-wrap gap-2">
        {KEYWORDS.map((k, i) => (
          <motion.span
            key={k.text}
            className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-sm font-extrabold"
            style={{
              border: `3px ${k.hit ? "solid" : "dashed"} ${k.hit ? GREEN : "#94a3b8"}`,
              color: k.hit ? GREEN : "#64748b",
            }}
            initial={{ opacity: 0, scale: 0.6 }}
            animate={{ opacity: on ? 1 : 0, scale: on ? 1 : 0.6 }}
            transition={reduce ? { duration: 0 } : { delay: 0.2 + i * 0.35, type: "spring", stiffness: 320, damping: 16 }}
          >
            {k.hit && <Check on={on} delay={0.5 + i * 0.35} color={GREEN} reduce={reduce} />}
            {k.text}
          </motion.span>
        ))}
      </div>
      <div className="mb-3 h-2.5 w-full overflow-hidden rounded-full" style={{ background: "#e2e8f0" }}>
        <motion.div
          className="h-full origin-left rounded-full"
          style={{ background: GREEN, width: "68%" }}
          initial={{ scaleX: 0 }}
          animate={{ scaleX: on ? 1 : 0 }}
          transition={t(1.4, 0.9)}
        />
      </div>
      <Pill tone="green" filled on={on} transition={t(2.2, 0.3)}>
        Ranked, not rejected
      </Pill>
    </Card>
  );
}

function CompetencyCard({ enabled }: { enabled: boolean }) {
  const { ref, on, reduce, t } = useSequence(enabled);
  return (
    <Card
      innerRef={ref}
      on={on}
      reduce={reduce}
      label="Competencies"
      term="Competencies"
      definition="These are the attributes and experiences the role requires, but are not tied to an exact keyword."
      fix="We match every one to proof in your profile. No other tool does this."
    >
      <div className="relative">
        <p className="text-sm font-semibold" style={{ color: "#64748b" }}>
          Job post requires
        </p>
        <motion.p
          className="mb-1 font-extrabold"
          style={{ color: NAVY }}
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: on ? 1 : 0, x: on ? 0 : -20 }}
          transition={t(0.2, 0.4)}
        >
          Vendor management
        </motion.p>

        <svg viewBox="0 0 24 40" className="mx-auto my-1 block h-10 w-6 overflow-visible" aria-hidden>
          <motion.path
            d="M12 2 L12 34 M5 27 L12 35 L19 27"
            fill="none"
            stroke={GREEN}
            strokeWidth={3}
            strokeLinecap="round"
            strokeLinejoin="round"
            initial={{ pathLength: 0 }}
            animate={{ pathLength: on ? 1 : 0 }}
            transition={reduce ? { duration: 0 } : { delay: 0.8, duration: 0.7, ease: "easeInOut" }}
          />
        </svg>

        <p className="text-sm font-semibold" style={{ color: "#64748b" }}>
          Your resume says
        </p>
        <motion.p
          className="mb-4 font-extrabold"
          style={{ color: "#1e293b" }}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: on ? 1 : 0, x: on ? 0 : 20 }}
          transition={t(1.2, 0.4)}
        >
          Negotiated supplier contracts
        </motion.p>
      </div>
      <Pill tone="green" filled on={on} transition={t(1.8, 0.3)}>
        Counts as a match
      </Pill>
    </Card>
  );
}

export function LandingAtsScreens() {
  const ref = useRef<HTMLDivElement>(null);
  const started = useInView(ref, { once: true, amount: 0.1 });
  // How many cards have had their turn: 1 on arrival, then one more every CARD_GAP_MS.
  const [turns, setTurns] = useState(0);

  useEffect(() => {
    if (!started) return;
    const timers = [0, 1, 2].map((i) => setTimeout(() => setTurns(i + 1), i * CARD_GAP_MS));
    return () => timers.forEach(clearTimeout);
  }, [started]);

  return (
    <div ref={ref} className="grid grid-cols-1 gap-6 md:grid-cols-3">
      <KnockoutCard enabled={turns >= 1} />
      <KeywordCard enabled={turns >= 2} />
      <CompetencyCard enabled={turns >= 3} />
    </div>
  );
}
